import { alpha, type Theme } from '@mui/material/styles';

/**
 * The primary brand gradient — used on hero CTAs, badges, and avatar icons.
 * Prefers `theme.dhBrandGradient` (authored `PPThemeGradient` from Theme Manager /
 * theme-core). Falls back to primary → deep/primary.dark so facility themes still
 * repaint when no authored gradient is set.
 *
 * Usage:
 *   sx={(theme) => ({ background: brandGradient(theme) })}
 */
export const brandGradient = (theme: Theme) => {
  if (theme.dhBrandGradient) return theme.dhBrandGradient;
  const end = theme.palette.deep ?? theme.palette.primary.dark;
  return `linear-gradient(135deg, ${theme.palette.primary.main}, ${end})`;
};

export type StatusTone = 'success' | 'warning' | 'error' | 'info' | 'primary';

export interface StatusToneSoft {
  bgcolor: string;
  color: string;
  borderColor: string;
  /** Convenience alias for Chip/Avatar borders that take a CSS border string. */
  border: string;
  /** Stronger accent for icons / dots (palette main). */
  accent: string;
}

/**
 * Soft status/info fills derived from the active theme palette so chips and
 * banners follow Theme Manager semantic colors instead of hardcoded Tailwind hex.
 */
export function statusToneSoft(theme: Theme, tone: StatusTone): StatusToneSoft {
  const main =
    tone === 'primary' ? theme.palette.primary.main : theme.palette[tone].main;
  const dark =
    tone === 'primary' ? theme.palette.primary.dark : theme.palette[tone].dark;
  // On dark surfaces, palette.*.dark is often too dim; use main for chip label contrast.
  const label = theme.palette.mode === 'dark' ? main : dark;
  return {
    bgcolor: alpha(main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
    color: label,
    borderColor: alpha(main, 0.35),
    border: `1px solid ${alpha(main, 0.35)}`,
    accent: main,
  };
}
